import { useState } from 'react';

import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { Autocomplete } from 'shared/mui/Autocomplete/Autocomplete';
import { Chip } from 'shared/mui/Chips';

import {
  FormControlStyled,
  TextFieldStyled,
  TooltipStyled,
} from './GatewayRulesFormRuleExpressionMultipleInput.styled';

const HINT_TEXT = 'Введите Id и нажмите Enter';

type Props<T extends FieldValues> = {
  name: Path<T>;
};

export function GatewayRulesFormRuleExpressionMultipleInput<T extends FieldValues>({
  name,
}: Props<T>): JSX.Element {
  const { control } = useFormContext();
  const [isDuplicate, setIsDuplicate] = useState(false);

  return (
    <FormControlStyled>
      <Controller
        control={control}
        defaultValue={[] as any}
        name={name}
        render={({ field, fieldState }) => {
          const values: string[] = Array.isArray(field.value) ? field.value : [];
          const hasValues = values.length > 0;
          const errorText = isDuplicate ? 'Идентификатор уже добавлен' : fieldState.error?.message;

          return (
            <Autocomplete
              {...field}
              clearOnBlur
              freeSolo
              multiple
              onBlur={() => {
                setIsDuplicate(false);
                field.onBlur();
              }}
              onChange={(event, data) => {
                setIsDuplicate(false);
                field.onChange((data as string[]).map((item) => item.trim()).filter(Boolean));
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') {
                  setIsDuplicate(false);

                  return;
                }

                event.preventDefault();
                const nextValue = (event.target as HTMLInputElement).value?.trim();

                setIsDuplicate(!!nextValue && values.includes(nextValue));
              }}
              options={[] as string[]}
              renderInput={(params) => (
                <TextFieldStyled
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: hasValues && (
                      <TooltipStyled placement="top" tooltipText={HINT_TEXT} />
                    ),
                  }}
                  error={!!errorText}
                  hasValues={hasValues}
                  helperText={errorText}
                  placeholder={hasValues ? undefined : HINT_TEXT}
                  size="small"
                  variant="outlined"
                />
              )}
              renderTags={(tags, getTagProps) =>
                tags.map((tag, index) => {
                  const { key, ...tagProps } = getTagProps({ index });

                  return <Chip {...tagProps} key={key} label={tag} size="small" />;
                })
              }
              value={values}
            />
          );
        }}
      />
    </FormControlStyled>
  );
}
